// tslint:disable
/**
 * TC API
 * TestCollab is a test management tool that helps development teams carry out testing effectively and avoid failures. # Introduction This is an API documentation for TestCollab 2.   # Authentication TestCollab supports two forms of authentication:   - An <b>API token</b> is required to make API calls. The token can be generated from user's profile; to obtain it login to your TestCollab account and go to ['My Profile Settings'](https://testcollab.io/my_profile) and switch to API Token tab. Click on \"Generate new API token\", Once the token is generated it can be sent as a query parameter for the API calls made - https://api.testcollab.io/projects?token=<generated-API-token>    - <b>Bearer authentication</b> (also called token authentication) is an HTTP authentication scheme that involves security tokens called bearer tokens. You can generate bearer auth token using [Login](#operation/login) API.     Send this token as the Authorization header while making requests to protected resources:     `Authorization: Bearer <token>`    For making API calls, we recommend using the <b>API token</b> generated from user profile page as it won't affect the on-going user session and will be available until it is revoked by the user who generated it.      # Error Handling Error responses   - `Not Found` - Error is sent when requested resource is not available.   - `Unauthorized - invalid token` - Error is sent when API token sent with the request is invalid or revoked. Error contains status code 401        - `Forbidden` - Error is sent when logged in user is not part of the company, project or his role does not allow to complete given action. Response consist of 403 status code and more information is given in error message.
 *
 * OpenAPI spec version: 2.3.1
 * Contact: support@testcollab.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { exists } from "../runtime";
import {
  CustomFieldCollectionOutput,
  CustomFieldCollectionOutputFromJSON,
  CustomFieldCollectionOutputToJSON,
  Requirement,
  RequirementFromJSON,
  RequirementToJSON,
  Step,
  StepFromJSON,
  StepToJSON,
  StepParsed,
  StepParsedFromJSON,
  StepParsedToJSON,
  Suite,
  SuiteFromJSON,
  SuiteToJSON,
  Tag,
  TagFromJSON,
  TagToJSON,
  Upload,
  UploadFromJSON,
  UploadToJSON,
  UserMinified,
  UserMinifiedFromJSON,
  UserMinifiedToJSON,
} from "./";

/**
 *
 * @export
 * @interface TestCase
 */
export interface TestCase {
  /**
   *
   * @type {number}
   * @memberof TestCase
   */
  id: number;
  /**
   * Per-company human-readable business number (shown as TC-204). Distinct from the internal id.
   * @type {number}
   * @memberof TestCase
   */
  displayNumber?: number;
  /**
   *
   * @type {string}
   * @memberof TestCase
   */
  title: string;
  /**
   *
   * @type {Suite}
   * @memberof TestCase
   */
  suite?: Suite;
  /**
   * Test case description (generated by rich text editor)
   * @type {string}
   * @memberof TestCase
   */
  description: string;
  /**
   *
   * @type {Array<Step>}
   * @memberof TestCase
   */
  steps?: Array<Step>;
  /**
   *
   * @type {Array<StepParsed>}
   * @memberof TestCase
   */
  stepsParsed?: Array<StepParsed>;
  /**
   * Priority, 0 - Low, 1- Normal, 2 - High
   * @type {number}
   * @memberof TestCase
   */
  priority: TestCasePriorityEnum;
  /**
   *
   * @type {Array<Tag>}
   * @memberof TestCase
   */
  tags?: Array<Tag>;
  /**
   *
   * @type {Array<Requirement>}
   * @memberof TestCase
   */
  requirements?: Array<Requirement>;
  /**
   *
   * @type {Array<Upload>}
   * @memberof TestCase
   */
  attachments?: Array<Upload>;
  /**
   * Array of custom fields
   * @type {Array<CustomFieldCollectionOutput>}
   * @memberof TestCase
   */
  customFields?: Array<CustomFieldCollectionOutput>;
  /**
   *
   * @type {number}
   * @memberof TestCase
   */
  sortOrder?: number;
  /**
   *
   * @type {string}
   * @memberof TestCase
   */
  createdAt?: string;
  /**
   *
   * @type {string}
   * @memberof TestCase
   */
  updatedAt?: string;
  /**
   *
   * @type {UserMinified}
   * @memberof TestCase
   */
  createdBy?: UserMinified;
  /**
   * Last run status of the test case
   * @type {string}
   * @memberof TestCase
   */
  lastRunStatus?: string;
  /**
   *
   * @type {number}
   * @memberof TestCase
   */
  runCount?: number;
  /**
   *
   * @type {number}
   * @memberof TestCase
   */
  avgExecutionTime?: number;
  /**
   *
   * @type {string}
   * @memberof TestCase
   */
  lastRunOn?: string;
  /**
   * Test failure percentage
   * @type {number}
   * @memberof TestCase
   */
  failureRate?: number;
  /**
   * TCV-7033. True when the test case is archived. An archived case is left out of the default test case lists and cannot be added to a test plan, while its revisions, executions and results are kept. Archive and restore it with the `archive` and `unarchive` actions of `POST /testcases/bulkAction`.
   * @type {boolean}
   * @memberof TestCase
   */
  archived?: boolean;
  /**
   * TCV-7051. ID of the project Trash entry while the test case is in Trash, null otherwise. Ordinary test case reads leave out a case in Trash, so the value is set only where a record keeps its link to the case, for example the `testcase` of a defect (`GET /defects/{id}`).
   * @type {number}
   * @memberof TestCase
   */
  readonly trashEntry?: number;
  /**
   * True when a BDD sync (`tc sync`) created the test case from a scenario of a `.feature` file in Git. The repository owns the case, so it cannot be edited, archived, restored or deleted by hand. TCV-7034: when its scenario is removed from the repository, the sync archives the case; when the scenario comes back with the same steps, the sync restores it. So a case with both `is_bdd_managed` and `archived` true was removed from the repository.
   * @type {boolean}
   * @memberof TestCase
   */
  readonly isBddManaged?: boolean;
  /**
   * Will be true only for links
   * @type {boolean}
   * @memberof TestCase
   */
  isReference?: boolean;
  /**
   * Only for links - source entity ID
   * @type {number}
   * @memberof TestCase
   */
  sourceId?: number;
  /**
   * Only for links - source Project ID
   * @type {number}
   * @memberof TestCase
   */
  sourceProject?: number;
  /**
   * 1 if test case is under review else 0  (applicable when test case review feature is enabled)
   * @type {number}
   * @memberof TestCase
   */
  underReview?: number;
  /**
   * Can be considered for execution by copilot (applicable when test case automation feature is enabled)
   * @type {number}
   * @memberof TestCase
   */
  isAutomated?: number;
  /**
   * TCV-6814. Whether automation covers this case. `observed` is set by TestCollab when a CI-sourced result matches the case, so coverage reflects what actually ran rather than what someone remembered to tick. `declared` is set by a person for automation that legitimately never reports in (performance, security, pipelines that are not wired up). Distinct from `is_automated`, which is a QA Copilot flag.
   * @type {string}
   * @memberof TestCase
   */
  automationCoverage?: TestCaseAutomationCoverageEnum;
  /**
   * TCV-6814. When a CI-sourced result last matched this case. Lets coverage age out, so a case stops counting once its automated test stops reporting.
   * @type {string}
   * @memberof TestCase
   */
  automationLastRunAt?: string;
  /**
   *
   * @type {UserMinified}
   * @memberof TestCase
   */
  automationDeclaredBy?: UserMinified;
  /**
   * Can be considered for execution by copilot (applicable when test case automation feature is enabled)
   * @type {number}
   * @memberof TestCase
   */
  qacTimeout?: number;
  /**
   *
   * @type {UserMinified}
   * @memberof TestCase
   */
  defaultAssignee?: UserMinified;
}

export function TestCaseFromJSON(json: any): TestCase {
  return {
    id: json["id"],
    displayNumber: !exists(json, "display_number")
      ? undefined
      : json["display_number"],
    title: json["title"],
    suite: !exists(json, "suite") ? undefined : SuiteFromJSON(json["suite"]),
    description: json["description"],
    steps: !exists(json, "steps")
      ? undefined
      : (json["steps"] as Array<any>).map(StepFromJSON),
    stepsParsed: !exists(json, "steps_parsed")
      ? undefined
      : (json["steps_parsed"] as Array<any>).map(StepParsedFromJSON),
    priority: json["priority"],
    tags: !exists(json, "tags")
      ? undefined
      : (json["tags"] as Array<any>).map(TagFromJSON),
    requirements: !exists(json, "requirements")
      ? undefined
      : (json["requirements"] as Array<any>).map(RequirementFromJSON),
    attachments: !exists(json, "attachments")
      ? undefined
      : (json["attachments"] as Array<any>).map(UploadFromJSON),
    customFields: !exists(json, "custom_fields")
      ? undefined
      : (json["custom_fields"] as Array<any>).map(
          CustomFieldCollectionOutputFromJSON
        ),
    sortOrder: !exists(json, "sort_order") ? undefined : json["sort_order"],
    createdAt: !exists(json, "created_at") ? undefined : json["created_at"],
    updatedAt: !exists(json, "updated_at") ? undefined : json["updated_at"],
    createdBy: !exists(json, "created_by")
      ? undefined
      : UserMinifiedFromJSON(json["created_by"]),
    lastRunStatus: !exists(json, "last_run_status")
      ? undefined
      : json["last_run_status"],
    runCount: !exists(json, "run_count") ? undefined : json["run_count"],
    avgExecutionTime: !exists(json, "avg_execution_time")
      ? undefined
      : json["avg_execution_time"],
    lastRunOn: !exists(json, "last_run_on") ? undefined : json["last_run_on"],
    failureRate: !exists(json, "failure_rate")
      ? undefined
      : json["failure_rate"],
    archived: !exists(json, "archived") ? undefined : json["archived"],
    trashEntry: !exists(json, "trash_entry") ? undefined : json["trash_entry"],
    isBddManaged: !exists(json, "is_bdd_managed")
      ? undefined
      : json["is_bdd_managed"],
    isReference: !exists(json, "is_reference")
      ? undefined
      : json["is_reference"],
    sourceId: !exists(json, "source_id") ? undefined : json["source_id"],
    sourceProject: !exists(json, "source_project")
      ? undefined
      : json["source_project"],
    underReview: !exists(json, "under_review")
      ? undefined
      : json["under_review"],
    isAutomated: !exists(json, "is_automated")
      ? undefined
      : json["is_automated"],
    automationCoverage: !exists(json, "automation_coverage")
      ? undefined
      : json["automation_coverage"],
    automationLastRunAt: !exists(json, "automation_last_run_at")
      ? undefined
      : json["automation_last_run_at"],
    automationDeclaredBy: !exists(json, "automation_declared_by")
      ? undefined
      : UserMinifiedFromJSON(json["automation_declared_by"]),
    qacTimeout: !exists(json, "qac_timeout") ? undefined : json["qac_timeout"],
    defaultAssignee: !exists(json, "default_assignee")
      ? undefined
      : UserMinifiedFromJSON(json["default_assignee"]),
  };
}

export function TestCaseToJSON(value?: TestCase): any {
  if (value === undefined) {
    return undefined;
  }
  return {
    id: value.id,
    display_number: value.displayNumber,
    title: value.title,
    suite: SuiteToJSON(value.suite),
    description: value.description,
    steps:
      value.steps === undefined
        ? undefined
        : (value.steps as Array<any>).map(StepToJSON),
    steps_parsed:
      value.stepsParsed === undefined
        ? undefined
        : (value.stepsParsed as Array<any>).map(StepParsedToJSON),
    priority: value.priority,
    tags:
      value.tags === undefined
        ? undefined
        : (value.tags as Array<any>).map(TagToJSON),
    requirements:
      value.requirements === undefined
        ? undefined
        : (value.requirements as Array<any>).map(RequirementToJSON),
    attachments:
      value.attachments === undefined
        ? undefined
        : (value.attachments as Array<any>).map(UploadToJSON),
    custom_fields:
      value.customFields === undefined
        ? undefined
        : (value.customFields as Array<any>).map(
            CustomFieldCollectionOutputToJSON
          ),
    sort_order: value.sortOrder,
    created_at: value.createdAt,
    updated_at: value.updatedAt,
    created_by: UserMinifiedToJSON(value.createdBy),
    last_run_status: value.lastRunStatus,
    run_count: value.runCount,
    avg_execution_time: value.avgExecutionTime,
    last_run_on: value.lastRunOn,
    failure_rate: value.failureRate,
    archived: value.archived,
    is_reference: value.isReference,
    source_id: value.sourceId,
    source_project: value.sourceProject,
    under_review: value.underReview,
    is_automated: value.isAutomated,
    automation_coverage: value.automationCoverage,
    automation_last_run_at: value.automationLastRunAt,
    automation_declared_by: UserMinifiedToJSON(value.automationDeclaredBy),
    qac_timeout: value.qacTimeout,
    default_assignee: UserMinifiedToJSON(value.defaultAssignee),
  };
}

/**
 * @export
 * @enum {string}
 */
export enum TestCasePriorityEnum {
  NUMBER_0 = 0,
  NUMBER_1 = 1,
  NUMBER_2 = 2,
}
/**
 * @export
 * @enum {string}
 */
export enum TestCaseAutomationCoverageEnum {
  None = "none",
  Observed = "observed",
  Declared = "declared",
}
